<template>
  <div class="step-container">
    <!-- Stepper Heading -->
    <StepperHeading :title="'Select Provider Company'" :step="2">
      <template #help>
        <p>Select the provider company that offers the selected solution. You can select a provider either by clicking on the map markers or by selecting from the list below.</p>
      </template>
    </StepperHeading>

    <!-- If loading providers, show a spinner or loading message -->
    <div v-if="isLoadingProviders" class="loading-container">
      <div class="spinner-container">
        <div class="spinner-border text-success" role="status">
          <span class="visually-hidden">Loading provider companies...</span>
        </div>
      </div>
      <p>Loading provider companies...</p>
    </div>

    <!-- If not loading, proceed -->
    <div v-else>
      <!-- Check if a solution has been selected -->
      <div v-if="selectedSolution">
        <!-- Check if we have any provider companies -->
        <div v-if="providerCompanies && providerCompanies.length > 0" class="providers-content">
          <!-- Interactive Map (with selected provider passed as prop) -->
          <div class="map-container">
            <CommonProviderMap
              :providers="mapProviders"
              :user-lat="userCoordinates.lat"
              :user-lon="userCoordinates.lon"
              :selected-provider="selectedProvider"
              mapTitle="Map of Available Providers"
              @provider-selected="selectProvider"
              ref="mapComponent"
            />
          </div>

          <!-- Selected Provider Details Card (Remains unchanged) -->
          <div v-if="selectedProvider" class="selected-provider-details">
            <div class="provider-card highlighted">
              <!-- Header for Selected Provider -->
              <div class="provider-card-header selected-details-header">
                <div class="provider-card-title">
                  <h3 class="provider-name">{{ getProviderName(selectedProvider) }}</h3>
                  <div class="provider-meta">
                    <span class="provider-category" v-if="getBusinessCategory(selectedProvider) && !isVcgProvider(selectedProvider)">
                      {{ getBusinessCategory(selectedProvider) }}
                    </span>
                    <span class="provider-distance" v-if="getDistance(selectedProvider)">
                      <i class="bi bi-geo-alt"></i> {{ getDistance(selectedProvider) }} km
                    </span>
                    <span class="selection-badge">
                      <i class="bi bi-check-circle-fill"></i> Selected provider
                    </span>
                  </div>
                </div>
              </div>
              <!-- Content for Selected Provider -->
              <div class="provider-card-content selected-details-content">
                <div class="provider-details">
                  <!-- Description -->
                  <div class="detail-section" v-if="hasDescription(selectedProvider)">
                    <div class="section-header">
                       <h4 class="section-title"><i class="bi bi-info-circle"></i> Description</h4>
                    </div>
                    <p class="description-text">{{ getDescription(selectedProvider) }}</p>
                  </div>

                  <!-- Institution Types - NEW -->
                  <div class="detail-section" v-if="hasInstitutionTypes(selectedProvider)">
                    <div class="section-header">
                      <i class="bi bi-building"></i>
                      <h4 class="section-title">Institution Types</h4>
                    </div>
                    <div class="tags-wrapper">
                      <span
                        v-for="(type, idx) in selectedProvider.institution_types"
                        :key="`type-${idx}`"
                        class="institution-tag"
                      >
                        {{ type }}
                      </span>
                    </div>
                  </div>
                  
                  <!-- Capacity - NEW -->
                  <div class="detail-section" v-if="hasCapacity(selectedProvider)">
                    <div class="section-header">
                      <i class="bi bi-box-seam"></i>
                      <h4 class="section-title">Food Capacity</h4>
                      <div class="section-hint">Types of food this provider needs and their capacity</div>
                    </div>
                    <div class="capacity-grid">
                      <div 
                        v-for="(item, idx) in selectedProvider.capacity" 
                        :key="`capacity-${idx}`"
                        class="capacity-item"
                      >
                        <div class="capacity-category">{{ item.food_category }}</div>
                        <div class="capacity-details">
                          <div class="capacity-status">
                            <span class="status-badge" :class="getNeededClass(item.needed)">
                              {{ item.needed }}
                            </span>
                          </div>
                          <div class="capacity-info" v-if="item.max_kg_per_week">
                            <span class="capacity-label">Maximum:</span>
                            <span class="capacity-value">{{ item.max_kg_per_week }} kg/week</span>
                          </div>
                          <div class="capacity-info" v-if="item.note">
                            <span class="capacity-label">Note:</span>
                            <span class="capacity-value">{{ item.note }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Logistics - NEW -->
                  <div class="detail-section" v-if="hasLogistics(selectedProvider)">
                    <div class="section-header">
                      <i class="bi bi-truck"></i>
                      <h4 class="section-title">Logistics</h4>
                      <div class="section-hint">Pickup and delivery options</div>
                    </div>
                    <div class="logistics-container">
                      <!-- Pickup Section -->
                      <div class="logistics-section" v-if="selectedProvider.logistics?.pickup">
                        <h5 class="logistics-title">
                          <i class="bi bi-box-arrow-up"></i>
                          Pickup
                        </h5>
                        <div class="logistics-details">
                          <div class="logistics-detail-item">
                            <span class="logistics-label">Available:</span>
                            <span class="logistics-value">{{ selectedProvider.logistics.pickup.available ? 'Yes' : 'No' }}</span>
                          </div>
                          <div class="logistics-detail-item" v-if="selectedProvider.logistics.pickup.radius_km">
                            <span class="logistics-label">Radius:</span>
                            <span class="logistics-value">{{ selectedProvider.logistics.pickup.radius_km }} km</span>
                          </div>
                          <div class="logistics-detail-item" v-if="selectedProvider.logistics.pickup.frequency">
                            <span class="logistics-label">Frequency:</span>
                            <span class="logistics-value">{{ selectedProvider.logistics.pickup.frequency }}</span>
                          </div>
                          <div class="logistics-detail-item">
                            <span class="logistics-label">Weekend Pickup:</span>
                            <span class="logistics-value">{{ selectedProvider.logistics.pickup.weekend ? 'Yes' : 'No' }}</span>
                          </div>
                        </div>
                      </div>
                      
                      <!-- Delivery Section -->
                      <div class="logistics-section" v-if="selectedProvider.logistics?.delivery">
                        <h5 class="logistics-title">
                          <i class="bi bi-box-arrow-in-down"></i>
                          Delivery
                        </h5>
                        <div class="logistics-details">
                          <div class="logistics-detail-item">
                            <span class="logistics-label">Accepted:</span>
                            <span class="logistics-value">{{ selectedProvider.logistics.delivery.accepted ? 'Yes' : 'No' }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Company Constraints -->
                  <div class="detail-section" v-if="hasCompanyConstraints(selectedProvider)">
                    <div class="section-header">
                       <h4 class="section-title"><i class="bi bi-shield-check"></i> Operational Requirements</h4>
                       <div class="section-hint">Any rules or conditions the provider requires for handling surplus</div>
                    </div>
                    <div class="constraints-grid">
                      <div class="constraint-item" v-if="getPreparation(selectedProvider)">
                        <div class="constraint-label">Preparation</div>
                        <div class="constraint-value">{{ getPreparation(selectedProvider) }}</div>
                      </div>
                      <div class="constraint-item" v-if="getMinimumQuantity(selectedProvider)">
                        <div class="constraint-label">Minimum Quantity</div>
                        <div class="constraint-value">{{ getMinimumQuantity(selectedProvider) }}</div>
                      </div>
                      <div class="constraint-item" v-if="getFrequency(selectedProvider)">
                        <div class="constraint-label">Frequency</div>
                        <div class="constraint-value">{{ getFrequency(selectedProvider) }}</div>
                      </div>
                      <div class="constraint-item" v-if="getAdditionalRequirements(selectedProvider)">
                        <div class="constraint-label">Additional Requirements</div>
                        <div class="constraint-value">{{ getAdditionalRequirements(selectedProvider) }}</div>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Accepted Food Types -->
                   <div class="detail-section" v-if="hasAcceptedFoodTypes(selectedProvider)">
                     <div class="section-header">
                       <h4 class="section-title"><i class="bi bi-basket"></i> Accepted Food Types</h4>
                       <div class="section-hint">Food categories and items this provider can work with</div>
                    </div>
                    <div class="food-types-grid">
                      <div class="food-type-category" v-if="hasFoodGroups(selectedProvider)">
                        <div class="category-label">Food Groups</div>
                        <div class="tags-wrapper">
                          <span v-for="(group, idx) in selectedProvider.accepted_food_types.food_group" :key="`group-${idx}`" class="food-tag">{{ group }}</span>
                        </div>
                      </div>
                      <div class="food-type-category" v-if="hasFoodSubgroups(selectedProvider)">
                        <div class="category-label">Food Subgroups</div>
                        <div class="tags-wrapper">
                          <span v-for="(subgroup, idx) in selectedProvider.accepted_food_types.food_subgroup" :key="`subgroup-${idx}`" class="food-tag">{{ subgroup }}</span>
                        </div>
                      </div>
                      <div class="food-type-category" v-if="hasSurplusNames(selectedProvider)">
                        <div class="category-label">Specific Surplus Items</div>
                        <div class="tags-wrapper">
                          <span v-for="(name, idx) in selectedProvider.accepted_food_types.surplus_name" :key="`surplus-${idx}`" class="food-tag">{{ name }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Business Categories - Only for VCG providers -->
                  <div class="detail-section" v-if="hasBusinessCategories(selectedProvider)">
                     <div class="section-header">
                       <h4 class="section-title"><i class="bi bi-briefcase"></i> Business Categories</h4>
                       <div class="section-hint">Sectors in which the provider operates</div>
                    </div>
                    <div class="business-categories">
                      <span v-for="(category, cIdx) in selectedProvider.businessCategory" :key="`category-${cIdx}`" class="business-category-tag">{{ category }}</span>
                    </div>
                  </div>
                  
                  <!-- Additional Company Information -->
                  <div class="detail-section" v-if="hasAdditionalInfo(selectedProvider)">
                     <div class="section-header">
                       <h4 class="section-title"><i class="bi bi-plus-circle"></i> Additional Information</h4>
                       <div class="section-hint">Additional details provided by the company</div>
                    </div>
                    <div class="additional-info">
                      <div v-for="(value, key) in filteredAdditionalInfo(selectedProvider)" :key="key" class="additional-item">
                        <div class="additional-label">{{ formatKey(key) }}</div>
                        <div class="additional-value" v-if="key === 'website'">
                          <a :href="formatUrl(value)" target="_blank" rel="noopener">{{ value }}</a>
                        </div>
                        <div class="additional-value" v-else>
                          {{ value }}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Contact Information -->
                  <div class="detail-section" v-if="hasContactInfo(selectedProvider)">
                     <div class="section-header">
                       <h4 class="section-title"><i class="bi bi-person-badge"></i> Contact Information</h4>
                    </div>
                    <div class="contact-info">
                      <div class="contact-item" v-if="getContactPerson(selectedProvider)">
                        <div class="contact-label">Contact Person</div>
                        <div class="contact-value">{{ getContactPerson(selectedProvider) }}</div>
                      </div>
                      <div class="contact-item" v-if="getEmail(selectedProvider)">
                        <div class="contact-label">Email</div>
                        <div class="contact-value">
                          <a :href="`mailto:${getEmail(selectedProvider)}`">{{ getEmail(selectedProvider) }}</a>
                        </div>
                      </div>
                      <div class="contact-item" v-if="getPhone(selectedProvider)">
                        <div class="contact-label">Phone</div>
                        <div class="contact-value">{{ getPhone(selectedProvider) }}</div>
                      </div>
                      <div class="contact-item" v-if="getLocation(selectedProvider)">
                        <div class="contact-label">Location</div>
                        <div class="contact-value">{{ formatAddress(getLocation(selectedProvider)) || 'Not specified' }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Provider Companies List (Not expandable) -->
          <div class="providers-list-container">
            <div class="list-header">
              <h5>
                <i class="bi bi-building me-2"></i>
                Available Provider Companies
              </h5>
              <span class="providers-count">{{ providerCompanies.length }} found</span>
            </div>

            <!-- Provider Cards List (Scrollable) - Updated to match SolutionSelector styling -->
            <div class="providers-list">
              <div
                v-for="(provider, index) in providerCompanies"
                :key="getProviderId(provider)"  
                class="list-item-card" 
                :class="{'selected': isSelected(provider)}"
                @click="selectProvider(provider)" 
              >
                <!-- Provider Card Header - Unified with SolutionSelector -->
                <div class="list-item-header"> 
                  <div class="item-index">{{ index + 1 }}</div>
                  <div class="item-info">
                    <!-- Use helper function for display name -->
                    <h3 class="item-name">{{ getProviderDisplayName(provider) }}</h3>
                    <!-- Use helper function for business category -->
                    <div class="item-categories" v-if="getBusinessCategory(provider) && !isVcgProvider(provider)">
                      <span class="category-badge">{{ getBusinessCategory(provider) }}</span>
                    </div>
                  </div>
                  <div class="item-actions">
                    <!-- Keep select button logic -->
                    <button
                      class="btn select-btn"
                      v-if="!isSelected(provider)"
                      @click.stop="selectProvider(provider)" 
                      title="Select this provider"
                    >
                      Select
                    </button>
                    <span class="selected-indicator" v-else title="Selected provider">
                      <i class="bi bi-check-circle-fill"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="empty-state">
          <div class="empty-state-icon">
            <i class="bi bi-building-x"></i>
          </div>
          <h3>No Provider Companies</h3>
          <p>No provider companies are available for the selected solution. Please select a different solution.</p>
        </div>
      </div>
      <div v-else class="alert-container">
        <div class="alert alert-info">
          <i class="bi bi-info-circle-fill me-2"></i>
          Please select a solution in the previous step before proceeding.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { onMounted, computed, ref, nextTick } from 'vue';
import { useStepperStore } from '@/stores/stepper';
import { useLCAStore } from '@/stores/lca';
import { usePageContextStore } from '@/stores/pageContext';
import StepperHeading from '@/components/Stepper/StepperHeading.vue';
import CommonProviderMap from '@/components/solution_details/providers/CommonProviderMap.vue';

export default {
  name: 'ProviderSelector',
  components: {
    StepperHeading,
    CommonProviderMap
  },
  setup() {
    const stepperStore = useStepperStore();
    const lcaStore = useLCAStore();
    const pageContextStore = usePageContextStore();
    const mapComponent = ref(null);

    // User coordinates (from company profile)
    const userCoordinates = ref({ lat: null, lon: null });

    // Get coordinates from stepper store if available
    if (stepperStore.companyProfile.location_geo &&
        stepperStore.companyProfile.location_geo.coordinates) {
      const coords = stepperStore.companyProfile.location_geo.coordinates; // [lon, lat]
      userCoordinates.value = {
        lat: coords[1],
        lon: coords[0]
      };
    }

    // Computed properties
    const selectedSolution = computed(() => lcaStore.lcaData.selectedSolution);
    const providerCompanies = computed(() => lcaStore.lcaData.providerCompanies);
    const selectedProvider = computed(() => lcaStore.lcaData.selectedProvider);
    const isLoadingProviders = computed(() => lcaStore.isLoadingProviders);

    // Get formatted providers for the map (remains the same)
    const mapProviders = computed(() => {
        if (!providerCompanies.value) return [];
        return providerCompanies.value.map(provider => {
            const isFoodcycle = !!provider.company_name;
            let coordinates = null;
            if (isFoodcycle && provider.location_geo?.type === 'Point' && Array.isArray(provider.location_geo.coordinates)) {
                coordinates = provider.location_geo.coordinates;
            } else if (!isFoodcycle && provider.location?.latitude && provider.location?.longitude) {
                coordinates = [provider.location.longitude, provider.location.latitude];
            }
            return {
                company_name: provider.company_name || provider.companyName || 'Unnamed Provider',
                description: provider.description || 'No description available',
                location_geo: coordinates ? { type: 'Point', coordinates } : null,
                provider_origin: provider.provider_origin || 'unknown',
                ...provider
            };
        });
    });


    // Select a provider
    function selectProvider(company) {
      // Avoid re-selecting the same provider unnecessarily
      if (isSelected(company)) return;

      lcaStore.setSelectedProvider(company);

      // Ensure map updates the selected provider marker
      nextTick(() => {
        // Use the appropriate method from your map component if it changed
        if (mapComponent.value && mapComponent.value.updateSelectedProvider) {
            mapComponent.value.updateSelectedProvider(company);
        } else if (mapComponent.value && mapComponent.value.highlightSelectedMarker) {
             mapComponent.value.highlightSelectedMarker(company); // Fallback if method name differs
        }
      });
    }

    // Format URL to ensure it has http(s)
    function formatUrl(url) {
      if (!url) return '#';
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
      }
      return `https://${url}`;
    }

    // Helper to get a unique ID for a provider (remains the same)
    function getProviderId(provider) {
        if (!provider) return null; // Handle null provider case
        return provider._id || provider.id || provider.company_name || provider.companyName || JSON.stringify(provider);
    }

    // Check if a provider is currently selected (remains the same)
    function isSelected(company) {
        if (!selectedProvider.value || !company) return false; // Add null check for company
        return getProviderId(company) === getProviderId(selectedProvider.value);
    }

    // Check if provider is from VCG (remains the same)
    function isVcgProvider(provider) {
      if (!provider) return false;
      return provider.companyName || provider.provider_origin === 'vcg.ai';
    }

    // Get provider display name based on type (remains the same)
    function getProviderDisplayName(provider) {
      if (!provider) return 'Unnamed Provider';
      return provider.company_name || provider.companyName || 'Unnamed Provider';
    }

    // Format address information (remains the same)
    function formatAddress(loc) {
      if (!loc) return '';
      const addr = loc.address || '';
      const city = loc.city || '';
      const postal = loc.postalCode || loc.postal_code || '';
      const country = loc.country || '';
      let parts = [addr, postal, city, country].filter(part => !!part);
      if (postal && city && parts.includes(postal) && parts.includes(city)) {
         parts = [addr, `${postal} ${city}`, country].filter(part => !!part);
      }
      let combined = parts.join(', ').trim();
      return combined || '';
    }
    
    // Format key for additional information (remains the same)
    function formatKey(key) {
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/_/g, ' ')
        .replace(/^./, str => str.toUpperCase())
        .trim().replace(/\s+/g, ' ');
    }

    // NEW: Helper for additional_company_information/infomation (typo in DB)
    function getAdditionalInfo(provider) {
      // Check both variants due to typo in the database
      if (provider.additional_company_information) {
        return provider.additional_company_information;
      }
      if (provider.additional_company_infomation) {
        return provider.additional_company_infomation;
      }
      return {};
    }
    
    // Filter additional info to exclude certain fields
    function filteredAdditionalInfo(provider) {
      const info = getAdditionalInfo(provider);
      const result = {};
      
      // Copy all properties except the excluded ones
      for (const key in info) {
        if (!['non_profit', 'umbrella_organization', 'opened_year'].includes(key)) {
          result[key] = info[key];
        }
      }
      
      return result;
    }

    // NEW helpers for the new fields
    const hasCapacity = (provider) => {
      return provider && Array.isArray(provider.capacity) && provider.capacity.length > 0;
    };

    const hasChallenges = (provider) => {
      return provider && Array.isArray(provider.challenges) && provider.challenges.length > 0;
    };

    const hasDigitalPlatforms = (provider) => {
      return provider && Array.isArray(provider.digital_platforms_used);
    };

    const hasDisposalMethods = (provider) => {
      return provider && Array.isArray(provider.disposal_methods) && provider.disposal_methods.length > 0;
    };

    const hasInstitutionTypes = (provider) => {
      return provider && Array.isArray(provider.institution_types) && provider.institution_types.length > 0;
    };

    const hasLogistics = (provider) => {
      return provider && provider.logistics && 
        (provider.logistics.pickup || provider.logistics.delivery);
    };

    // Helper for capacity statuses
    const getNeededClass = (needed) => {
      if (needed === 'always') return 'needed-always';
      if (needed === 'sometimes') return 'needed-sometimes';
      if (needed === 'rarely') return 'needed-rarely';
      return '';
    };

    // Unified helper functions for the SELECTED provider details card (remain the same)
    function getDescription(provider) { return provider?.description || ''; }
    function hasDescription(provider) { return !!getDescription(provider); }
    function getBusinessCategory(provider) {
        if (!provider) return '';
        if (provider.business_category) {
            return Array.isArray(provider.business_category) ? provider.business_category.join(', ') : provider.business_category;
        }
        if (Array.isArray(provider.businessCategory) && provider.businessCategory.length > 0) {
            return provider.businessCategory.join(', ');
        }
        return ''; // Return empty string if none found
    }
    function getLocation(provider) { return provider?.location || null; }
    function getDistance(provider) {
      if (provider?.dist?.calculated && typeof provider.dist.calculated === 'number') {
        return (provider.dist.calculated / 1000).toFixed(2);
      }
      if (provider?.dist_km && typeof provider.dist_km === 'number') {
        return provider.dist_km.toFixed(2);
      }
      return null;
    }
    function getContactPerson(provider) { return provider?.contact_person || provider?.contact?.name || ''; }
    function getEmail(provider) { return provider?.contact?.email || ''; }
    function getPhone(provider) { return provider?.contact?.phone || ''; }
    function hasContactInfo(provider) { return !!(getContactPerson(provider) || getEmail(provider) || getPhone(provider) || getLocation(provider)); }
    function getPreparation(provider) { return provider?.company_constraints?.preparation || ''; }
    function getMinimumQuantity(provider) { return provider?.company_constraints?.minimum_quantity_acceptance || ''; }
    function getFrequency(provider) { return provider?.company_constraints?.frequency || ''; }
    function getAdditionalRequirements(provider) { return provider?.company_constraints?.additional_requirements || ''; }
    function getProviderName(provider) { // Renamed from getProviderDisplayName for clarity on the detail card
      if (!provider) return 'Unknown Provider';
      return provider.company_name || provider.companyName || 'Unnamed Provider';
    }
    const hasCompanyConstraints = (provider) => {
      return !!provider?.company_constraints && (!!getPreparation(provider) || !!getMinimumQuantity(provider) || !!getFrequency(provider) || !!getAdditionalRequirements(provider));
    };
    const hasAcceptedFoodTypes = (provider) => {
        return !!provider?.accepted_food_types && (hasFoodGroups(provider) || hasFoodSubgroups(provider) || hasSurplusNames(provider));
    };
    const hasFoodGroups = (provider) => {
      return !!provider?.accepted_food_types?.food_group && Array.isArray(provider.accepted_food_types.food_group) && provider.accepted_food_types.food_group.length > 0;
    };
    const hasFoodSubgroups = (provider) => {
      return !!provider?.accepted_food_types?.food_subgroup && Array.isArray(provider.accepted_food_types.food_subgroup) && provider.accepted_food_types.food_subgroup.length > 0;
    };
    const hasSurplusNames = (provider) => {
      return !!provider?.accepted_food_types?.surplus_name && Array.isArray(provider.accepted_food_types.surplus_name) && provider.accepted_food_types.surplus_name.length > 0;
    };
    const hasBusinessCategories = (provider) => {
       return isVcgProvider(provider) && !!provider?.businessCategory && Array.isArray(provider.businessCategory) && provider.businessCategory.length > 0;
    };
    // Modified to check filtered additional info
    const hasAdditionalInfo = (provider) => {
      const filteredInfo = filteredAdditionalInfo(provider);
      return filteredInfo && Object.keys(filteredInfo).length > 0;
    };

    onMounted(() => {
      pageContextStore.setPageContext(
        'Select Provider Company',
        'On this page, the user selects one provider company that can process the chosen solution.',
        'We display providers on an interactive map and as a list. Selection can be made by clicking either on the map or in the list.'
      );
    });

    return {
      selectedSolution,
      providerCompanies,
      selectedProvider,
      isLoadingProviders,
      userCoordinates,
      mapProviders,
      selectProvider,
      getProviderId,
      isSelected,
      isVcgProvider,
      getProviderDisplayName, // Keep for list display
      formatAddress,
      formatKey,
      formatUrl,
      mapComponent,
      // Provider detail helper functions (kept for selected detail card)
      getDescription, hasDescription, getBusinessCategory, getLocation, getDistance,
      getContactPerson, getEmail, getPhone, hasContactInfo, getPreparation,
      getMinimumQuantity, getFrequency, getAdditionalRequirements, getProviderName, // Use specific name for detail card title
      hasCompanyConstraints, hasAcceptedFoodTypes, hasFoodGroups, hasFoodSubgroups,
      hasSurplusNames, hasBusinessCategories, hasAdditionalInfo, getAdditionalInfo, filteredAdditionalInfo,
      
      // NEW helpers for new fields
      hasCapacity,
      hasChallenges, 
      hasDigitalPlatforms,
      hasDisposalMethods,
      hasInstitutionTypes,
      hasLogistics,
      getNeededClass
    };
  },
};
</script>

<style scoped>
.step-container {
  padding: 2rem;
  border-radius: 12px;
  background-color: white;
}

/* Loading state */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 0;
  min-height: 300px;
}
.spinner-container { margin-bottom: 1rem; }
.spinner-border.text-success {
  color: #87b140 !important;
  width: 3rem;
  height: 3rem;
}

/* Content sections */
.providers-content {
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

/* Map container */
.map-container {
  width: 100%;
  height: 400px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(135, 177, 64, 0.1);
  transition: all 0.3s ease;
}
.map-container:hover {
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

/* Selected Provider Details Card Styles (Top Card) */
.selected-provider-details {
  margin-top: 1.5rem;
  margin-bottom: 1.5rem;
}

/* Base card style (applies to both types initially) */
.provider-card {
  background-color: white;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #eee;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
}

/* Highlight style only for the SELECTED details card */
.provider-card.highlighted {
  border: 2px solid #87b140;
  box-shadow: 0 0 0 3px rgba(135, 177, 64, 0.2), 0 8px 25px rgba(0, 0, 0, 0.1);
}

/* Header specific to SELECTED details card */
.provider-card-header.selected-details-header {
  padding: 1.25rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f2f5e7; /* Distinct background */
  border-bottom: 1px solid rgba(135, 177, 64, 0.2);
  min-height: 60px;
}

/* Content specific to SELECTED details card */
.provider-card-content.selected-details-content {
  padding: 0; /* Remove padding if sections have their own */
}

.provider-card-title { display: flex; flex-direction: column; gap: 0.5rem; }
.provider-name { margin: 0; font-size: 1.2rem; font-weight: 600; color: #333; line-height: 1.3; }
.provider-meta { display: flex; align-items: center; gap: 1rem; flex-wrap: wrap; }
.provider-category { font-size: 0.9rem; color: #666; }
.provider-distance { display: flex; align-items: center; gap: 0.25rem; font-size: 0.9rem; color: #666; }
.provider-distance i { color: #87b140; font-size: 1rem; }
.selection-badge { background-color: rgba(135, 177, 64, 0.15); color: #558000; padding: 0.3rem 0.7rem; border-radius: 30px; font-size: 0.85rem; font-weight: 600; display: inline-flex; align-items: center; gap: 0.4rem; }

/* Detail Sections within the SELECTED card */
.provider-details { display: flex; flex-direction: column; }
.detail-section { padding: 1.5rem; border-bottom: 1px solid #eee; }
.detail-section:last-child { border-bottom: none; }
.section-header { margin-bottom: 1.25rem; }
.section-header i { color: #87b140; font-size: 1.1rem; margin-right: 0.6rem; vertical-align: middle; }
.section-title { margin: 0 0 0.5rem 0; font-size: 1.1rem; font-weight: 600; color: #333; display: inline-block; vertical-align: middle; }
.section-hint { font-size: 0.85rem; color: #666; line-height: 1.5; margin-top: 0.3rem; display: block; }
.description-text { color: #555; line-height: 1.6; margin: 0; }

/* Tags and badges general styles */
.tags-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.institution-tag {
  background-color: rgba(135, 177, 64, 0.1);
  color: #558000;
  padding: 0.35rem 0.75rem;
  border-radius: 6px;
  font-size: 0.9rem;
  border: 1px solid rgba(135, 177, 64, 0.15);
}

/* NEW: Capacity styles */
.capacity-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
}

.capacity-item {
  background-color: #f9f9f9;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #eee;
}

.capacity-category {
  padding: 0.75rem 1rem;
  background-color: #f2f5e7;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #e0e0e0;
}

.capacity-details {
  padding: 1rem;
}

.capacity-status {
  margin-bottom: 0.75rem;
}

.status-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 16px;
  font-size: 0.85rem;
  font-weight: 500;
}

.needed-always {
  background-color: rgba(76, 175, 80, 0.15);
  color: #2e7d32;
}

.needed-sometimes {
  background-color: rgba(255, 193, 7, 0.15);
  color: #f57f17;
}

.needed-rarely {
  background-color: rgba(244, 67, 54, 0.15);
  color: #c62828;
}

.capacity-info {
  display: flex;
  margin-bottom: 0.5rem;
}

.capacity-info:last-child {
  margin-bottom: 0;
}

.capacity-label {
  font-weight: 600;
  color: #555;
  width: 80px;
  flex-shrink: 0;
}

.capacity-value {
  color: #333;
}

/* NEW: Logistics styles */
.logistics-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.25rem;
}

.logistics-section {
  background-color: #f9f9f9;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #eee;
}

.logistics-title {
  padding: 0.75rem 1rem;
  background-color: #f2f5e7;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #e0e0e0;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
}

.logistics-title i {
  color: #87b140;
}

.logistics-details {
  padding: 1rem;
}

.logistics-detail-item {
  margin-bottom: 0.75rem;
  display: flex;
  justify-content: space-between;
}

.logistics-detail-item:last-child {
  margin-bottom: 0;
}

.logistics-label {
  font-weight: 600;
  color: #555;
}

.logistics-value {
  color: #333;
}

/* Grids within the SELECTED card */
.constraints-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(230px, 1fr)); gap: 1.25rem; }
.constraint-item { display: flex; flex-direction: column; gap: 0.5rem; }
.constraint-label { font-weight: 600; color: #555; font-size: 0.9rem; }
.constraint-value { background-color: #f9f9f9; padding: 0.75rem 1rem; border-radius: 8px; color: #444; font-size: 0.95rem; line-height: 1.5; border-left: 3px solid #87b140; }
.food-types-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(230px, 1fr)); gap: 1.5rem; }
.food-type-category { display: flex; flex-direction: column; gap: 0.75rem; }
.category-label { font-weight: 600; color: #555; font-size: 0.9rem; }
.tags-wrapper { display: flex; flex-wrap: wrap; gap: 0.5rem; }
.food-tag { background-color: rgba(135, 177, 64, 0.1); color: #558000; padding: 0.35rem 0.75rem; border-radius: 20px; font-size: 0.85rem; border: 1px solid rgba(135, 177, 64, 0.2); line-height: 1.4; }
.business-categories { display: flex; flex-wrap: wrap; gap: 0.75rem; }
.business-category-tag { background-color: rgba(135, 177, 64, 0.1); color: #558000; padding: 0.4rem 0.85rem; border-radius: 6px; font-size: 0.9rem; font-weight: 500; }
.additional-info { display: grid; grid-template-columns: repeat(auto-fill, minmax(230px, 1fr)); gap: 1.25rem; }
.additional-item { display: flex; flex-direction: column; gap: 0.5rem; }
.additional-label { font-weight: 600; color: #555; font-size: 0.9rem; }
.additional-value { color: #333; font-size: 0.95rem; word-break: break-word; }
.additional-value a { color: #558000; text-decoration: none; transition: color 0.2s ease; }
.additional-value a:hover { color: #87b140; text-decoration: underline; }
.contact-info { display: grid; grid-template-columns: repeat(auto-fill, minmax(230px, 1fr)); gap: 1.25rem; }
.contact-item { display: flex; flex-direction: column; gap: 0.5rem; }
.contact-label { font-weight: 600; color: #555; font-size: 0.9rem; }
.contact-value { color: #333; font-size: 0.95rem; word-break: break-word; }
.contact-value a { color: #558000; text-decoration: none; transition: color 0.2s ease; }
.contact-value a:hover { color: #87b140; text-decoration: underline; }

/* Provider List Container */
.providers-list-container {
  margin-top: 1rem;
  width: 100%;
}
.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #eee;
}
.list-header h5 { margin: 0; font-weight: 700; color: #333; font-size: 1.2rem; display: flex; align-items: center; }
.providers-count {
  background-color: rgba(135, 177, 64, 0.15);
  color: #558000;
  font-weight: 600;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
}

/* Providers List - scrollable container */
.providers-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-height: 500px; /* Max height before scrolling */
  overflow-y: auto; /* Enable vertical scroll */
  padding: 0.5rem; /* Padding inside the scroll container */
  border: 1px solid #e0e0e0; /* Border around the list */
  border-radius: 8px;
  background-color: #fafafa; /* List background */
}

/* List Item Card - Unified with SolutionSelector */
.list-item-card {
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.06);
  border: 1px solid #e0e0e0;
  transition: all 0.2s ease-in-out;
  cursor: pointer;
  min-height: 70px;
  overflow: hidden;
}

.list-item-card:hover {
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
  border-color: #cccccc;
}

.list-item-card.selected {
  border-color: #87b140;
  box-shadow: 0 0 0 1px #87b140, 0 3px 8px rgba(135, 177, 64, 0.2);
  background-color: #f8fbf3;
}

.list-item-header {
  display: flex;
  align-items: center;
  padding: 0.9rem 1.25rem;
  background-color: transparent;
  transition: background-color 0.2s ease;
  min-height: 68px;
  width: 100%;
  box-sizing: border-box;
}

.item-index {
  width: 30px;
  height: 30px;
  background-color: #f0f0f0;
  color: #555;
  font-weight: 600;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  flex-shrink: 0;
  font-size: 0.9rem;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.list-item-card.selected .item-index {
  background-color: #87b140;
  color: white;
}

.item-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.25rem;
}

.item-name {
  font-size: 1.05rem;
  font-weight: 600;
  color: #333;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-categories {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.category-badge {
  display: inline-block;
  background-color: rgba(135, 177, 64, 0.1);
  color: #558000;
  padding: 0.2rem 0.6rem;
  border-radius: 15px;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.3;
  white-space: nowrap;
}

.item-actions {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  margin-left: 1rem;
}

.select-btn {
  background-color: white;
  color: #87b140;
  border: 1px solid #ccc;
  border-radius: 6px;
  padding: 0.35rem 0.75rem;
  font-weight: 600;
  font-size: 0.85rem;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.select-btn:hover {
  background-color: #f2f5e7;
  border-color: #87b140;
  color: #558000;
}

.selected-indicator {
  color: #87b140;
  font-size: 1.3rem;
  line-height: 1;
}

/* Empty state, Alert styles */
.empty-state { display: flex; flex-direction: column; align-items: center; text-align: center; padding: 3rem 2rem; margin-top: 1.5rem; background-color: #f9f9f9; border-radius: 12px; }
.empty-state-icon { background-color: #e9ecef; width: 70px; height: 70px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 1.8rem; color: #adb5bd; margin-bottom: 1.5rem; }
.empty-state h3 { color: #495057; font-weight: 600; margin-bottom: 0.75rem; font-size: 1.3rem; }
.empty-state p { color: #6c757d; max-width: 450px; margin-bottom: 0; line-height: 1.6; font-size: 0.95rem; }
.alert-container { margin-top: 2rem; }
.alert { border-radius: 10px; padding: 1.2rem 1.5rem; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05); border: none; display: flex; align-items: center; }
.alert i { font-size: 1.2rem; margin-right: 0.8rem; }
.alert-info { background-color: #e8f4f8; color: #0c5460; border-left: 4px solid #17a2b8; }

.empty-info {
  color: #888;
  font-style: italic;
  margin: 0;
}

/* Responsive Adjustments */
@media (max-width: 992px) {
  .step-container { padding: 1.5rem; }
  .map-container { height: 350px; }
  .providers-list { max-height: 450px; }
  .constraints-grid,
  .food-types-grid,
  .contact-info,
  .additional-info,
  .capacity-grid,
  .logistics-container { grid-template-columns: repeat(auto-fill, minmax(210px, 1fr)); }
}

@media (max-width: 768px) {
  .step-container { padding: 1.25rem; }
  .providers-content { gap: 1.5rem; }
  .map-container { height: 300px; }
  .list-item-header { padding: 0.8rem 1rem; }
  .item-name { font-size: 1rem; }
  .constraints-grid, .food-types-grid, .contact-info, .additional-info { grid-template-columns: 1fr; gap: 0.8rem; }
  .providers-list { max-height: 400px; }
  .item-actions { gap: 0.5rem; }
  .select-btn { padding: 0.3rem 0.6rem; font-size: 0.8rem; }
  .selected-indicator { font-size: 1.2rem; }
}

@media (max-width: 576px) {
  .step-container { padding: 1rem; }
  .map-container { height: 250px; }
  /* Adjust list item header for wrapping */
  .list-item-header { flex-wrap: wrap; padding: 0.75rem; min-height: 0; }
  .item-index { margin-right: 0.75rem; }
  .item-info { width: calc(100% - 40px - 0.75rem); margin-bottom: 0.5rem; order: 1; }
  .item-actions { width: 100%; margin-left: 0; justify-content: flex-end; margin-top: 0.5rem; order: 2; }
  .category-badge { font-size: 0.7rem; padding: 0.15rem 0.5rem; }
  .empty-state-icon { width: 60px; height: 60px; font-size: 1.5rem; }
  .empty-state h3 { font-size: 1.1rem; }
  .empty-state p { font-size: 0.9rem; }
  .providers-list { max-height: 350px; padding: 0.3rem; }
}
</style>